import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { ProgressSteps } from 'shared/ui/ProgressSteps'
import { Box } from 'shared/ui'
import { NewInboxContent } from 'entities/Phone/ui/AssignNumberCommon/ui/NewInboxContent/NewInboxContent'
import { HowAddNumberCards } from 'pages/settings/ui/HowAddInboxNumberCards/HowAddNumberCards'
import { type AssignMemberToInboxStore } from 'pages/settings/pages/members/ui/AssignMemberToInbox/store/assignMemberToInboxStore'
import { AssignMemberActions } from 'pages/settings/pages/members/ui/AssignMemberToInbox/ui/AssignMemberActions/AssignMemberActions'
import { PageDescription } from 'pages/settings/ui/PageDescription/PageDescription'
import { CommonNumbers } from 'pages/settings/pages/compliance/ui/CommonNumbers/CommonNumbers'
import { ChooseNumberCommon } from 'pages/settings/ui/ChooseNumberCommon/ChooseNumberCommon'
import { SelectAppAndNumbersCommon } from 'pages/settings/ui/SelectAppAndNumbersCommon/SelectAppAndNumbersCommon'
import { InboxCommonPayment } from 'pages/settings/ui/InboxCommonPayment/InboxCommonPayment'
import { type IAddNumberType } from 'pages/settings/store/newInboxSteps/newInboxCommonStepsStore'
import styles from './styles.module.scss'

type IAssignMemberToNewInboxProps = {
  store: AssignMemberToInboxStore
}

export const AssignMemberToNewInbox = observer(({ store }: IAssignMemberToNewInboxProps) => {
  useEffect(() => {
    void store.assignNewInboxStore.init()
  }, [])

  const {
    progressTitle,
    allStepsLength,
    settingsStep,
    currentStep,
    newInboxStore,
    setAddNumberType,
    nextStep,
    commonStore,
    stepLoading,
    createInbox,
    addNumberType,
    getLastStep,
    handleAddNumbersNextStep,
    nextDisabled: assignNewInboxNextDisabled,
  } = store.assignNewInboxStore
  const {
    unassignedNumbersCount,
    isGracePeriod,
    addSalesmsgNumbersStore,
    chooseNumberStore,
    selectAppAndNumbersStore,
    shouldShowPaymentNumbersList,
    isBuyingNumber,
    selectedNumbersCount,
    paymentStore,
  } = commonStore
  const { loading, numbersStore } = addSalesmsgNumbersStore
  const {
    chooseNumberTabsStore,
    useCases,
    selectedUseCaseId,
    nextDisabled,
    setUseCase,
    nextClick,
  } = chooseNumberStore
  const { multiSelectedValues: numbers } = numbersStore
  const appSelectedNumber = selectAppAndNumbersStore.selectedNumber
  const { isLoadingPriceInfo } = paymentStore
  const [selectedAddNumberType, setSelectedAddNumberType] = useState<IAddNumberType>(addNumberType)

  const { onCancel, isSeatRequired } = store

  const handleAssignInbox = async () => {
    try {
      await createInbox()
      setSelectedAddNumberType('default')
      onCancel?.()
    } catch {}
  }

  const handleBackOverride = () => {
    if (getLastStep === 'howAddNumber') {
      setSelectedAddNumberType('default')
    }
  }

  const renderActions = (
    handleNextClick: () => void,
    isDisabled?: boolean,
    isLoading?: boolean
  ) => {
    return (
      <div className={styles.actions}>
        <AssignMemberActions
          store={store}
          onAction={handleNextClick}
          disabled={isDisabled}
          loading={isLoading}
          onBackOverride={handleBackOverride}
        />
      </div>
    )
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 'newInbox':
        return (
          <>
            <div className={styles.section}>
              <NewInboxContent newInboxStore={newInboxStore} hideSubmit />
            </div>
            {renderActions(nextStep, assignNewInboxNextDisabled)}
          </>
        )
      case 'howAddNumber':
        return (
          <>
            <div className={styles.section}>
              <HowAddNumberCards
                setAddNumberType={setSelectedAddNumberType}
                unassignedNumbersCount={unassignedNumbersCount}
                isGracePeriod={isGracePeriod}
                hiddenKeys={['landline']}
                isModalMode
                selectedType={selectedAddNumberType}
              />
            </div>
            {renderActions(
              () => {
                setAddNumberType(selectedAddNumberType)
                nextStep()
              },
              selectedAddNumberType === 'default',
              stepLoading
            )}
          </>
        )
      case 'addSalesmsgNumbers':
        return (
          <div>
            <PageDescription>
              Your workspace has {unassignedNumbersCount} unassigned number
              {unassignedNumbersCount > 1 ? 's' : ''} available
            </PageDescription>
            <CommonNumbers
              loading={loading}
              numbersStore={numbersStore}
              isLabelQuestionTooltip={false}
            />
            {renderActions(
              () => handleAddNumbersNextStep(handleAssignInbox),
              numbersStore.isStepDisabled || stepLoading,
              loading || stepLoading
            )}
          </div>
        )
      case 'payment':
        return (
          <>
            <InboxCommonPayment
              salesmsgNumbers={numbers}
              appSelectedNumber={appSelectedNumber}
              shouldShowPaymentNumbersList={shouldShowPaymentNumbersList}
              isBuyingNumber={isBuyingNumber}
              licensesRequired={selectedNumbersCount}
              seatsRequired={isSeatRequired ? 1 : 0}
              paymentStore={paymentStore}
              chooseNumberTabsStore={chooseNumberTabsStore}
              isHideNextButton
            />
            {renderActions(handleAssignInbox, isLoadingPriceInfo || stepLoading, stepLoading)}
          </>
        )
      case 'chooseNumber':
        return (
          <>
            <ChooseNumberCommon
              chooseNumberTabsStore={chooseNumberTabsStore}
              useCases={useCases}
              selectedUseCaseId={selectedUseCaseId}
              setUseCase={setUseCase}
              isHideNextButton
            />
            {renderActions(nextStep, nextDisabled)}
          </>
        )
      case 'selectAppAndNumbers':
        return (
          <SelectAppAndNumbersCommon
            store={selectAppAndNumbersStore}
            nextClickHandler={nextClick}
            customButtons={(nextClickHandler, isDisabled, loading) =>
              renderActions(nextClickHandler, isDisabled, loading)
            }
          />
        )
      default:
        return null
    }
  }

  return (
    <Box marginTop='18px' minWidth='492px'>
      <ProgressSteps title={progressTitle} total={allStepsLength} step={settingsStep} />
      {renderStepContent()}
    </Box>
  )
})
